<script setup lang="ts">
// Shared OTP step — used by the login flow (`VerifyOtpContainer`) and the
// sign-up flow (`SignUpOtpContainer`).
//
// `showNavigation` decides who owns the BACK affordance: login renders it here
// because its card has no other chrome, while the sign-up card's container
// already renders one above the step. Exactly one BACK must be visible.

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import BaseButton from '@uiKit/elements/Button.vue'
import BaseInputOtp from '@uiKit/elements/InputOtp.vue'
import FormField from '@uiKit/components/FormField.vue'
import BackLink from '@uiKit/components/BackLink.vue'
import AuthTrustChips from './AuthTrustChips.vue'
import { ONE_SECOND_MS } from '@shared/utils'

interface Props {
  email?: string
  submitLabel?: string
  loading?: boolean
  showNavigation?: boolean
  canBack?: boolean
  apiError?: string | null
  /** Remaining verification attempts, surfaced next to the error when the server sends it. */
  attemptsLeft?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  email: '',
  submitLabel: 'Verify OTP',
  loading: false,
  showNavigation: false,
  canBack: true,
  attemptsLeft: null,
})

const emit = defineEmits<{
  (e: 'verify', code: string): void
  (e: 'resend'): void
  (e: 'back'): void
}>()

const RESEND_COOLDOWN_SECONDS = 30
const OTP_LENGTH = 6
const SECONDS_PER_MINUTE = 60

const otp = ref('')
const cooldown = ref(RESEND_COOLDOWN_SECONDS)
const clientError = ref<string | null>(null)
const hasEditedSinceApiError = ref(false)
let cooldownTimer: ReturnType<typeof setInterval> | null = null

const normalizedOtp = computed(() => otp.value.replace(/\D/g, '').slice(0, OTP_LENGTH))
const isOtpValid = computed(() => normalizedOtp.value.length === OTP_LENGTH)

const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : null,
)
const visibleError = computed(() => clientError.value ?? visibleApiError.value ?? null)

// "1 attempt left" reads better than "1 attempt(s) left" and only ever shows
// alongside a real error, so it never nags an untouched form.
const attemptsMessage = computed(() => {
  if (props.attemptsLeft === null || !visibleApiError.value) return null
  const plural = props.attemptsLeft === 1 ? 'attempt' : 'attempts'
  return `${props.attemptsLeft} ${plural} left`
})

const pad = (value: number) => String(value).padStart(2, '0')
const countdownLabel = computed(
  () =>
    `${pad(Math.floor(cooldown.value / SECONDS_PER_MINUTE))}:${pad(cooldown.value % SECONDS_PER_MINUTE)}`,
)

const startCooldown = () => {
  cooldown.value = RESEND_COOLDOWN_SECONDS
  if (cooldownTimer) clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    cooldown.value--
    if (cooldown.value <= 0 && cooldownTimer) {
      clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, ONE_SECOND_MS)
}

const handleVerify = () => {
  if (props.loading) return
  if (!isOtpValid.value) {
    clientError.value = `Please enter all ${OTP_LENGTH} digits of the code.`
    return
  }
  clientError.value = null
  emit('verify', normalizedOtp.value)
}

const handleResend = () => {
  if (cooldown.value > 0) return
  otp.value = ''
  clientError.value = null
  emit('resend')
  startCooldown()
}

onMounted(startCooldown)

onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer)
})

watch(
  otp,
  (value) => {
    const safeValue = String(value ?? '')
    if (safeValue !== normalizedOtp.value) otp.value = normalizedOtp.value
    if (clientError.value && isOtpValid.value) clientError.value = null
    hasEditedSinceApiError.value = true
  },
  { immediate: true },
)

watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)
</script>

<template>
  <Box class="otp-form" direction="col" gap="var(--auth-block-gap)">
    <BackLink v-if="props.showNavigation && props.canBack" @back="emit('back')" />

    <Box direction="col" gap="var(--auth-title-gap)">
      <Text is="h1" type="display-xl">
        <Text is="span" weight="medium">Verify Your </Text
        ><Text is="span" color="primary">Email Address</Text>
      </Text>

      <Box align="center" gap="2" class="otp-form__recipient">
        <Text type="heading" weight="regular" color="muted">
          A 6-digit code was sent to
          <Text is="span" type="heading" weight="semibold" color="default">
            {{ props.email || 'your email address' }}
          </Text>
        </Text>
        <BaseButton
          variant="text"
          color="secondary"
          size="small"
          icon="pencil"
          class="otp-form__edit"
          aria-label="Change email address"
          @click="emit('back')"
        />
      </Box>
    </Box>

    <Box direction="col" gap="4">
      <FormField :error="visibleError ?? undefined" :helperText="attemptsMessage ?? undefined">
        <BaseInputOtp v-model="otp" :length="OTP_LENGTH" class="otp-form__input" />
      </FormField>

      <Box align="center" gap="1" class="otp-form__resend">
        <Text v-if="cooldown > 0" type="body" weight="semibold" color="default">
          Resend OTP in {{ countdownLabel }}
        </Text>
        <BaseButton
          v-else
          variant="link"
          size="small"
          color="primary"
          label="Resend OTP"
          class="otp-form__resend-button"
          @click="handleResend"
        />
      </Box>

      <BaseButton
        block
        size="large"
        class="otp-form__submit"
        :label="props.submitLabel"
        :loading="props.loading"
        :disabled="props.loading"
        @click="handleVerify"
      />
    </Box>

    <AuthTrustChips />
  </Box>
</template>

<style scoped>
.otp-form__recipient {
  flex-wrap: wrap;
}

.otp-form__edit {
  padding: 0;
  min-width: 0;
}

.otp-form__submit {
  border-radius: var(--p-border-radius-md);
}

.otp-form__resend-button {
  padding: 0;
  min-width: 0;
}

/* The digit boxes share the card's left/right edges with the submit button —
   fixed widths would leave dead space inside the fixed-width card. */
.otp-form__input :deep(.p-inputotp) {
  display: flex;
  gap: 0.625rem;
  width: 100%;
}

.otp-form__input :deep(.p-inputotp-input) {
  flex: 1;
  min-width: 0;
  height: 52px;
  border-radius: var(--p-border-radius-md);
}
</style>
